/**
 * The shell: sidebar on desktop, bottom tab bar on phones.
 *
 * The breakpoint is `md` (768 px). Below it the navigation is a fixed bottom bar
 * with 44 px+ targets and a safe-area inset, because a top nav on a phone puts
 * every tap out of thumb reach; above it a sidebar, because a bottom bar on a
 * 27" monitor is absurd.
 *
 * ---------------------------------------------------------------------------
 * LAYOUT
 * ---------------------------------------------------------------------------
 * Content is capped at 1280 px and centred. An operator's eye cannot scan a
 * 2400 px measure; above the cap the page simply gets more margin, which is
 * what makes a wide display feel calm rather than empty.
 *
 * Icons are Lucide (see `components/icons.tsx`) — one grid, one stroke weight,
 * and decorative in every case: each one sits beside its own label.
 */

import type { ReactNode } from 'react';

import { relativeTime } from '../lib/format';
import { Icon, type IconName } from './icons';

export interface NavItem {
  path: string;
  label: string;
  icon: IconName;
}

export const NAV: NavItem[] = [
  { path: '/', label: 'Overview', icon: 'overview' },
  { path: '/nodes', label: 'Servers', icon: 'servers' },
  { path: '/services', label: 'Services', icon: 'services' },
  { path: '/databases', label: 'Databases', icon: 'databases' },
  { path: '/errors', label: 'Errors', icon: 'errors' },
  { path: '/findings', label: 'Findings', icon: 'findings' },
];

function isActive(current: string, path: string): boolean {
  if (path === '/') return current === '/';
  return current === path || current.startsWith(`${path}/`);
}

export function Layout({
  path,
  navigate,
  title,
  eyebrow,
  lastLoadedAt,
  refreshing,
  onReload,
  onOpenKey,
  unauthenticatedMode,
  children,
}: {
  path: string;
  navigate: (to: string) => void;
  title: string;
  /** The small line above the title — where you are, in the estate's terms. */
  eyebrow?: string;
  lastLoadedAt?: Date | null;
  refreshing?: boolean;
  onReload?: () => void;
  onOpenKey?: () => void;
  unauthenticatedMode: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="min-h-dvh bg-bg text-ink">
      <a
        href="#main"
        className="skip-link sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface-2 focus:px-3 focus:py-2"
      >
        skip to content
      </a>

      <div className="md:flex">
        {/* Desktop sidebar */}
        <nav
          aria-label="sections"
          className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-line px-3 py-5 md:flex"
        >
          <div className="mb-7 flex items-center gap-2.5 px-3">
            <Icon name="overview" size={18} className="text-ink-3" />
            <div className="min-w-0">
              <p className="text-sm font-semibold tracking-tight">mount&#8209;watch</p>
              <p className="mt-0.5 text-2xs tracking-[0.06em] text-ink-3">Mount Hetzner cluster</p>
            </div>
          </div>

          <ul className="space-y-1">
            {NAV.map((item) => {
              const active = isActive(path, item.path);
              return (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(item.path);
                    }}
                    aria-current={active ? 'page' : undefined}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors ${
                      active
                        ? 'bg-surface-2 font-medium text-ink'
                        : 'text-ink-3 hover:bg-surface/70 hover:text-ink-2'
                    }`}
                  >
                    <Icon name={item.icon} size={17} className={active ? '' : 'text-ink-4'} />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {onOpenKey !== undefined && (
            <div className="mt-auto px-1">
              <button
                type="button"
                onClick={onOpenKey}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 text-left text-xs text-ink-3 transition-colors hover:text-ink-2"
              >
                <Icon name="key" size={15} />
                reading key
              </button>
            </div>
          )}
        </nav>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-20 border-b border-line/70 bg-bg/90 backdrop-blur-md">
            <div className="mx-auto flex max-w-320 items-center justify-between gap-4 px-4 py-4 sm:px-6">
              <div className="min-w-0">
                {eyebrow !== undefined && (
                  <p className="text-2xs font-medium uppercase tracking-[0.1em] text-ink-3">
                    {eyebrow}
                  </p>
                )}
                <h1 className="mt-0.5 truncate text-lg font-semibold tracking-tight">{title}</h1>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <p className="hidden items-center gap-1.5 pr-2 text-2xs text-ink-3 sm:flex">
                  <span
                    aria-hidden="true"
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      refreshing === true ? 'pulse-live bg-accent' : 'bg-ink-4'
                    }`}
                  />
                  {refreshing === true
                    ? 'refreshing'
                    : lastLoadedAt === undefined || lastLoadedAt === null
                      ? 'not loaded'
                      : relativeTime(lastLoadedAt.toISOString())}
                </p>
                {onOpenKey !== undefined && (
                  <button
                    type="button"
                    onClick={onOpenKey}
                    aria-label="reading key"
                    className="flex items-center justify-center rounded-lg px-2.5 text-ink-3 transition-colors hover:text-ink md:hidden"
                  >
                    <Icon name="key" size={17} />
                  </button>
                )}
                {onReload !== undefined && (
                  <button
                    type="button"
                    onClick={onReload}
                    aria-label="reload"
                    className="flex items-center justify-center rounded-lg px-2.5 text-ink-3 transition-colors hover:text-ink"
                  >
                    <Icon name="reload" size={17} />
                  </button>
                )}
              </div>
            </div>

            {/*
              The security banner.
              It is a real warning and it stays, but it is now one quiet line
              with a rule and an open-padlock rather than a full-width hatched
              slab. The previous version was the loudest object on the page,
              which trained the eye to skip the top of the screen entirely.
            */}
            {unauthenticatedMode && (
              <div
                role="status"
                className="mx-auto flex max-w-320 items-center gap-2 border-l-2 border-warn/60 px-4 py-1.5 text-2xs leading-snug text-ink-3 sm:px-6"
              >
                <Icon name="insecure" size={13} className="text-warn" />
                <span>
                  <span className="font-semibold text-warn">MW_UI_AUTH=none</span> — anyone who can
                  reach this port can read everything here.
                </span>
              </div>
            )}
          </header>

          <main
            id="main"
            data-enter
            key={path}
            className="mx-auto max-w-320 space-y-5 px-4 py-5 pb-28 sm:px-6 md:space-y-6 md:py-6 md:pb-12"
            // The bottom bar is 60 px + safe area; the padding above clears it.
          >
            {children}
          </main>
        </div>
      </div>

      {/* Phone tab bar */}
      <nav
        aria-label="sections"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur-md md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <ul className="grid grid-cols-6">
          {NAV.map((item) => {
            const active = isActive(path, item.path);
            return (
              <li key={item.path}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(item.path);
                  }}
                  aria-current={active ? 'page' : undefined}
                  className={`flex h-15 w-full flex-col items-center justify-center gap-1 text-[10px] leading-none transition-colors ${
                    active ? 'font-medium text-ink' : 'text-ink-3'
                  }`}
                >
                  {/* Not colour alone: the active tab also gets a bar. */}
                  <span
                    aria-hidden="true"
                    className={`h-0.5 w-5 rounded-full ${active ? 'bg-ink' : 'bg-transparent'}`}
                  />
                  <Icon name={item.icon} size={19} />
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
